// app/javascript/components/shared/ui/Modal.js
//
// Reusable modal shell — full-screen backdrop, centered dialog, Escape +
// backdrop-click to dismiss. Header is split into eyebrow / title (slots);
// body and footer accept arbitrary children. Promoted from
// quotes/qline_groups/components/Modal.js so the V3 builder can adopt the
// same shell instead of inlining its own.
import React, { useEffect } from "react"
import { cva } from "class-variance-authority"
import clsx from "clsx"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import IconButton from "./IconButton"

export const modalDialogVariants = cva(
  "bg-white rounded-2xl w-full my-4 border border-slate-200",
  {
    variants: {
      size: {
        sm:  "max-w-md",
        md:  "max-w-2xl",
        lg:  "max-w-4xl",
        xl:  "max-w-6xl",
      },
    },
    defaultVariants: { size: "xl" },
  }
)

const Modal = ({
  isOpen,
  onClose,
  eyebrow,
  title,
  footer,
  size,
  className,
  children,
}) => {
  useEffect(() => {
    if (!isOpen) return
    const onKey = e => { if (e.key === "Escape") onClose() }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-slate-900/40 backdrop-blur-sm p-6 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className={clsx(modalDialogVariants({ size }), className)}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-6 pb-4 border-b border-slate-200">
          <div>
            {eyebrow && <div className="text-xs text-slate-500 font-medium">{eyebrow}</div>}
            <h2 className="text-xl font-semibold text-slate-900 mt-0.5">{title}</h2>
          </div>
          <IconButton onClick={onClose} title="Fermer" size="md">
            <FontAwesomeIcon icon="xmark" />
          </IconButton>
        </div>

        <div className="p-6 space-y-6 max-h-[calc(100vh-220px)] overflow-y-auto">
          {children}
        </div>

        {footer && (
          <div className="flex items-center justify-between p-4 border-t border-slate-200 bg-slate-50/50 rounded-b-2xl">
            {footer}
          </div>
        )}
      </div>
    </div>
  )
}

export default Modal
